import { Box, Button, Flex, Grid, Heading, Icon, Link, LinkBox, Stack, StackProps } from "@biblioteksentralen/react";
import { getPath, ResolvedEmployee } from "@libry-content/common";
import { ExternalLink, Mail, Phone } from "react-feather";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useSitePalette } from "../../utils/useSitePalette";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { formatPhoneNumber } from "../contactInfo/formatPhoneNumber";
import { Edit } from "../editInSanity/EditInSanity";
import { ResolvedLibrarySummary } from "../library/sanityQuery";
import ContactAvatar from "./ContactAvatar";
import EmployeeCard from "./EmployeeCard";
import LibraryCard from "./LibraryCard";

const ContactCard = ({ employeeOrLibrary }: { employeeOrLibrary?: ResolvedEmployee | ResolvedLibrarySummary }) => {
  if (!employeeOrLibrary) return null;

  return employeeOrLibrary._type === "employee" ? (
    <EmployeeCard employee={employeeOrLibrary} hidePositionAndAffiliations />
  ) : (
    <LibraryCard library={employeeOrLibrary} />
  );
};

type ContactCardWrapperProps = {
  employeeOrLibrary: ResolvedEmployee | ResolvedLibrarySummary;
  name: string;
  email?: string;
  phone?: string;
  contactFormUrl?: string;
  positionAndAffiliations?: React.ReactNode;
};

export const ContactCardWrapper = ({
  employeeOrLibrary,
  name,
  email,
  phone,
  contactFormUrl,
  positionAndAffiliations,
}: ContactCardWrapperProps) => {
  const paletteColor = useSitePalette().colors.card;

  return (
    <Grid
      as={LinkBox}
      gridTemplateAreas={{
        base: `
        "image"
        "image"
        "header"
        "contact"
      `,
        sm: `
        "_ image"
        "header image"
        "contact contact"
      `,
      }}
      gridTemplateColumns={{ sm: "3fr 2fr" }}
      gridTemplateRows={{ sm: "2rem auto 1fr" }}
      gap="1rem"
      role="group"
      color={paletteColor.css.color}
    >
      <Box
        {...paletteColor.css}
        gridColumn="1/3"
        gridRow={{ base: "2/5", sm: "2/4" }}
        borderRadius={{ sm: ".3rem" }}
        transition=".3s"
        _groupHover={{ boxShadow: "md" }}
      />
      <Flex
        gridArea="image"
        justifyContent="flex-start"
        alignItems="flex-start"
        paddingRight="1rem"
        paddingLeft={{ base: "1rem", sm: "0" }}
      >
        <ContactAvatar employeeOrLibrary={employeeOrLibrary} size="9rem" resolution={300} fontSize="xl" hideCaption />
      </Flex>
      <Stack gridArea="header" padding={{ base: "0 1.5rem", sm: "1.5rem 1.5rem 0" }} justifyContent="end">
        <Heading as="h2" size="md">
          <InternalLinkOverlay href={getPath(employeeOrLibrary)} _groupHover={{ textDecoration: "underline" }}>
            {name}
          </InternalLinkOverlay>
        </Heading>
        <Edit doc={employeeOrLibrary} />
        {positionAndAffiliations}
      </Stack>
      <ContactInfo
        email={email}
        phone={phone}
        contactFormUrl={contactFormUrl}
        padding="0 1.5rem 1.5rem"
        gridArea="contact"
      />
    </Grid>
  );
};

export const ContactInfo = ({
  email,
  phone,
  contactFormUrl,
  ...chakraProps
}: { email?: string; phone?: string; contactFormUrl?: string } & StackProps) => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="flex-start" {...chakraProps}>
      {email && <LinkWithIcon href={`mailto:${email}`} icon={Mail} label={email} />}
      {phone && <LinkWithIcon href={`tel:${formatPhoneNumber(phone)}`} icon={Phone} label={formatPhoneNumber(phone)} />}
      {contactFormUrl && <ContactFormLink contactFormUrl={contactFormUrl} label={t("Ta kontakt")} />}
    </Stack>
  );
};

export const ContactFormLink = (props: { contactFormUrl?: string; label: string }) => (
  <Button
    rightIcon={<Icon as={ExternalLink} size="1.25em" />}
    variant="secondary"
    as="a"
    fontSize="sm"
    href={props.contactFormUrl}
  >
    {props.label}
  </Button>
);

const LinkWithIcon = (props: { href: string; label: string; icon: typeof Mail }) => (
  <Link variant="plain" href={props.href} display="flex" gap=".5rem" alignItems="center" overflowWrap="anywhere">
    <Icon as={props.icon} size="1em" /> {props.label}
  </Link>
);

export default ContactCard;
